import { useRef, useState, type JSX } from 'react'
import { SessionExpiredError, type QueryResult } from '../api'

// The SQL tab: a front end for the governed POST /query endpoint, shown only to
// a finance-viewer who also holds finance-query. The endpoint is the boundary,
// not this component: SELECT-only, one statement, run as finance_ro (no
// per-person compensation, no sync_rejects), with a timeout and a row cap. This
// just saves a developer from pasting a bearer token into curl to look at the
// views, e.g. to check what label strings the data actually carries.

const EXAMPLES: { label: string; sql: string }[] = [
  {
    label: 'freee labels',
    sql: `SELECT effective_label, length(effective_label) AS chars, count(*) AS vendors
FROM freee_recurring_costs
GROUP BY effective_label
ORDER BY vendors DESC`,
  },
  {
    label: 'Readable views',
    sql: `SELECT table_name
FROM information_schema.role_table_grants
WHERE grantee = 'finance_ro' AND privilege_type = 'SELECT'
ORDER BY table_name`,
  },
  {
    label: 'Source freshness',
    sql: `SELECT job, last_succeeded_at, last_run_at, last_run_status
FROM source_freshness
ORDER BY job`,
  },
]

const DEFAULT_SQL = EXAMPLES[0]?.sql ?? ''

function cellText(value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function QueryConsole({
  run,
  onSessionExpired,
  initialResult = null,
}: {
  run: (statement: string) => Promise<QueryResult>
  onSessionExpired: (message: string) => void
  // Pre-rendered result for the static preview, which cannot run a query.
  initialResult?: QueryResult | null
}): JSX.Element {
  const [sql, setSql] = useState(DEFAULT_SQL)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<QueryResult | null>(initialResult)
  const [error, setError] = useState<string | null>(null)
  // Synchronous in-flight flag: `running` is state, so a held or double-tapped
  // Ctrl+Enter would read it stale and post the statement more than once.
  const inFlight = useRef(false)

  const execute = async (): Promise<void> => {
    if (inFlight.current || sql.trim() === '') return
    inFlight.current = true
    setRunning(true)
    setError(null)
    try {
      setResult(await run(sql))
    } catch (cause) {
      if (cause instanceof SessionExpiredError) {
        onSessionExpired(cause.message)
        return
      }
      // A failed run replaces the previous result rather than sitting under it:
      // an old table beneath a new error reads as this statement's answer.
      setResult(null)
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      inFlight.current = false
      setRunning(false)
    }
  }

  return (
    <section className="panel">
      <h2>SQL</h2>
      <p className="chart-sub">
        Read-only queries against the finance views, run as <code>finance_ro</code> through the governed query
        endpoint: one SELECT per run, a statement timeout, and 1,000 rows by default. The per-person pay tables
        and quarantined rows are not readable, but results are still confidential finance data: some views
        (sheet-consistency issues among them) can imply one person&rsquo;s pay, so don&rsquo;t share them
        outside the finance team. <kbd>Ctrl</kbd>/<kbd>⌘</kbd> + <kbd>Enter</kbd> runs.
      </p>
      <div className="query-examples">
        {EXAMPLES.map((example) => (
          <button key={example.label} type="button" className="row-edit" onClick={() => setSql(example.sql)}>
            {example.label}
          </button>
        ))}
      </div>
      <textarea
        className="query-input"
        aria-label="SQL statement"
        spellCheck={false}
        rows={8}
        value={sql}
        onChange={(e) => setSql(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            void execute()
          }
        }}
      />
      <div className="query-actions">
        <button type="button" className="primary" disabled={running || sql.trim() === ''} onClick={() => void execute()}>
          {running ? 'Running…' : 'Run'}
        </button>
        {result !== null && (
          <span className="muted">
            {result.rowCount} row{result.rowCount === 1 ? '' : 's'} · {result.elapsedMs} ms
            {result.truncated && ' · truncated at the row cap'}
          </span>
        )}
      </div>
      {error !== null && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {result !== null && result.columns.length > 0 && (
        <div className="scroller">
          <table className="query-result">
            <thead>
              <tr>
                {result.columns.map((column, i) => (
                  <th scope="col" key={`${i}:${column}`}>
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((value, c) => {
                    const text = cellText(value)
                    return <td key={c}>{text === null ? <span className="muted">null</span> : text}</td>
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
